/**
 * Review Questionnaire - Card components for approval status and risk/score display.
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  CheckCircle2,
  XCircle,
  MessageSquare,
  Loader2,
  Target,
  CircleDot,
  Circle,
  ChevronRight,
} from 'lucide-react';
import type { RiskAssessment } from '../../lib/api';
import type { ApprovalStatusData } from './reviewQuestionnaireUtils';
import { useFormatters } from '../../contexts/PreferencesContext';

/* ------------------------------------------------------------------ */
/*  Multi-Level Approval Status Card                                   */
/* ------------------------------------------------------------------ */

interface ApprovalStatusCardProps {
  approvalStatus: ApprovalStatusData;
  loadingApproval: boolean;
}

export function ApprovalStatusCard({ approvalStatus, loadingApproval }: ApprovalStatusCardProps) {
  const { formatDate } = useFormatters();
  if (approvalStatus.total_levels <= 1) return null;

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <ChevronRight className="h-5 w-5" />
            Approval Progress
          </CardTitle>
          <Badge variant={approvalStatus.is_complete ? 'default' : approvalStatus.is_rejected ? 'destructive' : 'secondary'}>
            {approvalStatus.is_complete ? 'Complete' : approvalStatus.is_rejected ? 'Rejected' : `Level ${approvalStatus.current_level || 1} of ${approvalStatus.total_levels}`}
          </Badge>
        </div>
        <CardDescription>
          This questionnaire requires {approvalStatus.total_levels}-level approval
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {approvalStatus.levels.map((level, idx) => (
            <div key={level.level} className="flex items-center gap-3">
              <div className="flex-shrink-0">
                {level.decision === 'approved' ? (
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                ) : level.decision === 'rejected' ? (
                  <XCircle className="h-6 w-6 text-red-500" />
                ) : level.decision === 'request_info' ? (
                  <MessageSquare className="h-6 w-6 text-amber-500" />
                ) : approvalStatus.current_level === level.level ? (
                  <CircleDot className="h-6 w-6 text-blue-500" />
                ) : (
                  <Circle className="h-6 w-6 text-gray-300" />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Level {level.level} Approval</span>
                  {level.decision === 'pending' && approvalStatus.current_level === level.level && (
                    <Badge variant="outline" className="text-blue-600 border-blue-300">
                      Awaiting Review
                    </Badge>
                  )}
                  {level.decision === 'approved' && (
                    <Badge variant="outline" className="text-green-600 border-green-300">
                      Approved
                    </Badge>
                  )}
                  {level.decision === 'rejected' && (
                    <Badge variant="outline" className="text-red-600 border-red-300">
                      Rejected
                    </Badge>
                  )}
                </div>
                {level.reviewer && (
                  <p className="text-sm text-muted-foreground">
                    {level.decision !== 'pending' ? `${level.decision === 'approved' ? 'Approved' : 'Reviewed'} by ${level.reviewer}` : ''}
                    {level.decided_at && ` on ${formatDate(level.decided_at)}`}
                  </p>
                )}
                {level.comments && (
                  <p className="text-sm text-muted-foreground mt-1 italic">&quot;{level.comments}&quot;</p>
                )}
              </div>

              {idx < approvalStatus.levels.length - 1 && (
                <div className="absolute left-3 top-8 h-full w-px bg-gray-200" />
              )}
            </div>
          ))}
        </div>
        {loadingApproval && (
          <div className="flex items-center justify-center py-2 mt-3">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Loading approval status...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
/*  Score & Risk Assessment Card                                       */
/* ------------------------------------------------------------------ */

interface ScoreRiskCardProps {
  riskAssessment: RiskAssessment;
  loadingRisk: boolean;
}

export function ScoreRiskCard({ riskAssessment, loadingRisk }: ScoreRiskCardProps) {
  // A template that has no scoring weights configured returns
  // max_possible_score === 0. Showing "Total Score 0%", "Pass Threshold 0%"
  // and "0.0 / 0.0 Points Earned" reads as broken data rather than as
  // "this template isn't scored". Drop the scoring cells entirely in that
  // case and relabel the card — the risk-level colouring still applies
  // because risk can be derived from answers without a numeric score.
  const hasScoring = riskAssessment.max_possible_score > 0;
  const gridCols = hasScoring ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1';
  return (
    <Card className={`border-l-4 ${
      riskAssessment.risk_level === 'low' ? 'border-l-green-500' :
      riskAssessment.risk_level === 'medium' ? 'border-l-yellow-500' :
      riskAssessment.risk_level === 'high' ? 'border-l-orange-500' :
      'border-l-red-500'
    }`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5" />
            {hasScoring ? 'Score & Risk Assessment' : 'Risk Assessment'}
          </CardTitle>
          {hasScoring && (
            <Badge variant={riskAssessment.passed ? 'default' : 'destructive'}>
              {riskAssessment.passed ? 'Passed' : 'Failed'}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className={`grid ${gridCols} gap-4 mb-4`}>
          {hasScoring && (
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-3xl font-bold text-primary">{riskAssessment.score.toFixed(0)}%</p>
              <p className="text-xs text-muted-foreground">Total Score</p>
            </div>
          )}
          {hasScoring && (
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-3xl font-bold">{riskAssessment.pass_score}%</p>
              <p className="text-xs text-muted-foreground">Pass Threshold</p>
            </div>
          )}
          {hasScoring && (
            <div className="text-center p-3 bg-muted/50 rounded-lg">
              <p className="text-xl font-bold">
                {riskAssessment.total_score.toFixed(1)} / {riskAssessment.max_possible_score.toFixed(1)}
              </p>
              <p className="text-xs text-muted-foreground">Points Earned</p>
            </div>
          )}
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Badge variant="outline" className={`text-sm ${
              riskAssessment.risk_level === 'low' ? 'text-green-600 border-green-300 bg-green-50' :
              riskAssessment.risk_level === 'medium' ? 'text-yellow-600 border-yellow-300 bg-yellow-50' :
              riskAssessment.risk_level === 'high' ? 'text-orange-600 border-orange-300 bg-orange-50' :
              'text-red-600 border-red-300 bg-red-50'
            }`}>
              {riskAssessment.risk_display.label}
            </Badge>
            <p className="text-xs text-muted-foreground mt-1">Risk Level</p>
          </div>
        </div>

        {hasScoring && riskAssessment.section_breakdown && riskAssessment.section_breakdown.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Section Breakdown</p>
            <div className="space-y-2">
              {riskAssessment.section_breakdown.map((section) => (
                <div key={section.section_id} className="flex items-center justify-between text-sm">
                  <span className="truncate">{section.section_name}</span>
                  <div className="flex items-center gap-2">
                    <Progress value={section.percentage ? Number(section.percentage) : 0} className="w-24 h-2" />
                    <span className={`font-medium min-w-[3rem] text-right ${
                      Number(section.percentage) >= 80 ? 'text-green-600' :
                      Number(section.percentage) >= 60 ? 'text-yellow-600' :
                      Number(section.percentage) >= 40 ? 'text-orange-600' :
                      'text-red-600'
                    }`}>
                      {section.percentage ? Number(section.percentage).toFixed(0) : 0}%
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingRisk && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Loading score...</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
